// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import { CustomAppState } from "@bg/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetShippingMethod";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { RadioPresentationProps } from "@insite/mobius/Radio";
import { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
    return {
        cart,
        isCartLoading,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        // XNG-Change
        nameOfCarrier: state.xCheckout.nameOfCarrier,
        accountNumber: state.xCheckout.accountNumber,
        shippingNotes: state.xCheckout.shippingNotes,
        showErrors: state.xCheckout.showErrors,
        isPickUp: cart?.fulfillmentMethod === FulfillmentMethod.PickUp,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    // XNG-Change
    resetPropertiesOfCarrier: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xCheckoutNameOfCarrier/update",
            nameOfCarrier: "",
        } as any);
        dispatch({
            type: "xCheckoutAccountNumber/update",
            accountNumber: "",
        } as any);
        dispatch({
            type: "xCheckoutShippingNotes/update",
            shippingNotes: "",
        } as any);
    },
    updateNameOfCarrier: (nameOfCarrier: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xCheckoutNameOfCarrier/update",
            nameOfCarrier,
        } as any);
    },
    updateAccountNumber: (accountNumber: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xCheckoutAccountNumber/update",
            accountNumber,
        } as any);
    },
    updateShippingNotes: (shippingNotes: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xCheckoutShippingNotes/update",
            shippingNotes,
        } as any);
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDeliveryMethodStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    fulfillmentMethodText?: TypographyPresentationProps;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    servicesGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    localDeliveryMessage?: InjectableCss;
    // XNG-Changes
    notesTextAreaStyle?: TextAreaPresentationProps;
}

export const onePageCheckoutDeliveryMethodStyles: OnePageCheckoutDeliveryMethodStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
    fulfillmentMethodText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    servicesGridItem: { width: 12 },
    deliveryDateGridItem: { width: 12 },
    pickUpDateGridItem: { width: 12 },
};

const styles = onePageCheckoutDeliveryMethodStyles;

const OnePageCheckoutDeliveryMethod = ({
    cart,
    showShippingMethod,
    setShippingMethod,
    // XNG-Change
    nameOfCarrier,
    accountNumber,
    shippingNotes,
    resetPropertiesOfCarrier,
    updateNameOfCarrier,
    updateAccountNumber,
    updateShippingNotes,
    showErrors,
    isPickUp,
}: Props) => {
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null | undefined>();
    useEffect(() => {
        if (cart) {
            setSelectedShipVia(cart.shipVia);
            setIsUpdatingCart(false);
            // XNG-Change
            setSelectedCarrier(cart.carrier);
        }
    }, [cart?.carrier, cart?.shipVia]);

    // XNG-Change
    // Coming from multi-step checkout
    useEffect(() => {
        if (cart && cart?.properties) {
            updateNameOfCarrier(nameOfCarrier || cart.properties.carrierName || "");
            updateAccountNumber(accountNumber || cart.properties.accountNumber || "");
            updateShippingNotes(shippingNotes || cart.properties.shippingNotes || "");
        }
    }, [cart?.properties]);

    // XNG-Change
    // No need for the spinner here
    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return null;
    }

    // XNG-Change
    const serviceChangeHandler: React.ChangeEventHandler<HTMLSelectElement> = (
        event: ChangeEvent<HTMLSelectElement>,
    ) => {
        if (!cart.carriers) {
            return;
        }

        // XNG-Change
        const shipVias = selectedCarrier?.shipVias;
        const shipVia = shipVias?.find(o => o.id === event.currentTarget.value);
        if (!shipVia) {
            return;
        }

        // const carrier = cart.carriers.find(o => o.shipVias?.some(p => p.id === shipVia.id));
        if (!shipVia || !selectedCarrier) {
            return;
        }

        setIsUpdatingCart(true);
        setShippingMethod({ carrier: selectedCarrier, shipVia });
        setSelectedShipVia(shipVia);
    };

    // XNG-Change
    const carrierChangeHandler: React.ChangeEventHandler<HTMLSelectElement> = (
        event: ChangeEvent<HTMLSelectElement>,
    ) => {
        if (!cart.carriers) {
            return;
        }

        const carrier = cart.carriers.find(o => o.id === event.currentTarget.value);
        const shipVia = carrier?.shipVias?.[0];
        if (!carrier || !shipVia) {
            return;
        }

        resetPropertiesOfCarrier();
        setIsUpdatingCart(true);
        setShippingMethod({ carrier, shipVia });
        setSelectedShipVia(shipVia);
        setSelectedCarrier(carrier);
    };

    /**
     * XNG-Changes
     * We use RequestDeliveryDate for Delivery and Pickup, we don't need to use RequestPickUpDate
     */
    // const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
    //     setRequestedPickUpDate({
    //         requestedPickUpDate: selectedDay,
    //         requestedPickUpDateDisabled: selectedDayDisabled,
    //     });
    // };
    // const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
    //     setRequestedDeliveryDate({
    //         requestedDeliveryDate: selectedDay,
    //         requestedDeliveryDateDisabled: selectedDayDisabled,
    //     });
    // };

    // XNG-Change

    const carriersWithAdditionalFieldsIds = cart?.properties ? cart?.properties?.carriersWithAdditionalFieldsIds : "";

    const carriersWithAdditionalFieldsIdsList =
        (carriersWithAdditionalFieldsIds && carriersWithAdditionalFieldsIds.split(",")) || "";

    const nameOfCarrierChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateNameOfCarrier(event.currentTarget.value);
    };

    const accountNumberChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateAccountNumber(event.currentTarget.value);
    };

    const shippingNotesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        updateShippingNotes(event.currentTarget.value);
    };

    /**
     * XNG-Changes
     * We need to check if pickup by session & Cart to handle orderApproval case.
     * Case: when have a different fulfillmentMethod between cart and session.
     */
    if (!showShippingMethod || isPickUp) {
        return null;
    }

    /**
     * XNG-Change
     * BG-2729
     * Removed the following code:
     * Edit button redirect to cart - moved to OnePageCheckoutContainer widget
     * onePageCheckout_fulfillmentMethod message - Removed from design
     * requested delivery date - moved to OnePageCheckoutOrderDetails widget
     * requested pick up date - moved to OnePageCheckoutOrderDetails widget
     * Change the title to "Shipping"
     * Remove Ship Via keeping the carrier only in select dropdown - Design and old multiple steps doesn't show them
     * Remove allowEmptyShipping from condition by FRD request
     */
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.titleText}>{translate("Shipping")}</Typography>
            {cart.properties.localDeliveryId && cart.properties.localDeliveryId === selectedCarrier?.id && (
                <StyledWrapper {...styles.localDeliveryMessage}>
                    <Notify messageType="warning" body={siteMessage("Custom_Cart_LocalDelivery")} />
                </StyledWrapper>
            )}
            {/* Xng-Change remove !allowEmptyShipping from the condition */}
            {cart.carriers?.length === 0 && (
                <StyledWrapper {...styles.noCarriersFoundGridItem}>
                    <Notify messageType="danger" title="Error" body={siteMessage("ReviewAndPay_NoCarriersFound")} />
                </StyledWrapper>
            )}
            {cart.carriers.length > 0 && (
                <StyledWrapper {...styles.servicesGridItem}>
                    <Select
                        required
                        onChange={carrierChangeHandler}
                        value={selectedCarrier?.id ?? ""}
                        data-test-selector="onePageCheckout_chooseSelectCarrier"
                        aria-label="Select Carrier"
                        label={translate("Select Carrier")}
                    >
                        {cart.carriers.map(carrier => (
                            <option
                                key={`carrier-${carrier.description}`}
                                value={`${carrier.id}`}
                                data-test-selector={`carrier-${carrier.description}`}
                            >
                                {carrier.description}
                            </option>
                        ))}
                    </Select>
                    <Select
                        required
                        onChange={serviceChangeHandler}
                        value={selectedShipVia?.id}
                        data-test-selector="onePageCheckout_chooseSelectService"
                        aria-label="Select Service"
                        label={translate("Select Service")}
                        disabled={!selectedCarrier?.shipVias || selectedCarrier.shipVias.length <= 1}
                    >
                        {selectedCarrier?.shipVias?.map(shipVia => (
                            <option
                                key={shipVia?.id}
                                value={shipVia?.id}
                                data-test-selector={`${selectedCarrier.description} - ${shipVia?.description}`}
                            >
                                {shipVia.description}
                            </option>
                        ))}
                    </Select>
                    {selectedCarrier &&
                        selectedCarrier.id &&
                        carriersWithAdditionalFieldsIdsList &&
                        !!carriersWithAdditionalFieldsIdsList?.length &&
                        carriersWithAdditionalFieldsIdsList.includes(selectedCarrier.id) && (
                            <>
                                <StyledWrapper>
                                    <StyledWrapper className="item-content-wrapper">
                                        <TextField
                                            label={translate("Name Of Carrier")}
                                            value={nameOfCarrier}
                                            maxLength={300}
                                            onChange={nameOfCarrierChangeHandler}
                                            placeholder={translate("Carrier Name")}
                                            required
                                            error={
                                                showErrors &&
                                                !nameOfCarrier.trim() &&
                                                translate("this field is required")
                                            }
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper>
                                    <StyledWrapper className="item-content-wrapper">
                                        <TextField
                                            label={translate("Account Number")}
                                            value={accountNumber}
                                            maxLength={300}
                                            onChange={accountNumberChangeHandler}
                                            placeholder={translate("Account Number")}
                                            required
                                            error={
                                                showErrors &&
                                                !accountNumber.trim() &&
                                                translate("this field is required")
                                            }
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper>
                                    <StyledWrapper className="item-content-wrapper">
                                        <TextArea
                                            label={translate("Shipping Notes")}
                                            value={shippingNotes}
                                            maxLength={300}
                                            onChange={shippingNotesChangeHandler}
                                            placeholder={translate("You can start typing here")}
                                            {...styles.notesTextAreaStyle}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDeliveryMethod),
    definition: {
        displayName: "Delivery Method",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
